import {
  timeRangeToBounds,
  agentFirstName,
  type SummaryRow,
  type MeasureSummary,
  summariseMeasures,
} from "./AgentProfileView.helpers";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeft, ArrowRight } from "lucide-react";
import { type DeltaPanelRow } from "../components/MeasureDeltaPanel.helpers";
import { MeasureDeltaPanel } from "../components/MeasureDeltaPanel";

import {
  api,
  type AgentAggregate,
  type AgentAssessmentResultRow,
  type AgentCallRow,
  type AgentCallSlice,
  type AgentCallsOut,
  type AgentProfile,
  type AgentRow,
  type AssessmentRunStatus,
  type CustomDateRange,
  type RangeMode,
  type Role,
  type TimeRange,
  type UserOut,
} from "../lib/api";
import { agentLookupKey, agentSlug, initialsOf } from "../lib/agents.helpers";

import { encodeAgentFrom } from "../lib/router";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useTimeRange } from "../lib/useTimeRange";
import { rangeTitle } from "../components/TimeRangeControls.helpers";
import { TimeRangeControls } from "../components/TimeRangeControls";

import { MeasureBarChart, type MeasureFocus } from "../components/MeasureBarChart";
import { CallCountCaption } from "../components/CallCountCaption";
import { AssessmentEmptyState } from "../components/AssessmentEmptyState";
import { CollapsibleSection } from "../components/CollapsibleSection";
import { relativeTime } from "../lib/format";
import { formatDeltaPp } from "../lib/triage";
import { Tooltip } from "../components/Tooltip";
import { type MetricId } from "../components/MetricTooltips.helpers";
import { LabelWithTooltip } from "../components/MetricTooltips";

import { TrainingTipsModal } from "../components/TrainingTipsModal";

function fmtMmSs(ms: number | null): string {
  if (ms == null) return "—";
  const totalSeconds = Math.round(ms / 1000);
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtStarted(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function KpiTile({
  label,
  value,
  valueClass,
  sub,
  onClick,
  active,
  metricId,
}: {
  label: string;
  value: string;
  valueClass?: string;
  sub?: React.ReactNode;
  onClick?: () => void;
  active?: boolean;
  // Task #316 — canonical metric id used to render an info indicator
  // next to the tile label; copy comes from the shared map.
  metricId?: MetricId;
}) {
  const clickable = typeof onClick === "function";
  const base =
    "rounded-lg border bg-card p-4 shadow-md animate-card-enter text-left w-full";
  const border = active
    ? "border-primary ring-2 ring-primary/30"
    : "border-border";
  const interactive = clickable
    ? " transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    : "";
  const body = (
    <>
      <div className="text-sm text-muted-foreground">
        {metricId ? (
          <LabelWithTooltip metricId={metricId}>{label}</LabelWithTooltip>
        ) : (
          label
        )}
      </div>
      <div
        className={
          "mt-2 text-4xl font-bold tabular-nums " + (valueClass ?? "text-foreground")
        }
        style={{ fontFamily: "var(--app-font-serif)" }}
      >
        {value}
      </div>
      {sub != null && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </>
  );
  if (clickable) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={!!active}
        className={`${base} ${border}${interactive}`}
      >
        {body}
      </button>
    );
  }
  return <div className={`${base} ${border}`}>{body}</div>;
}

function AgentProfileSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-full bg-muted" />
        <div className="flex-1 space-y-2">
          <div className="h-6 w-48 bg-muted rounded" />
          <div className="h-3 w-64 bg-muted rounded" />
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-lg border border-border bg-card p-4 h-28" />
        ))}
      </div>
      <div className="h-3 w-40 bg-muted rounded" />
      <div className="rounded-lg border border-border bg-card h-24" />
      <div className="h-3 w-32 bg-muted rounded" />
      <div className="rounded-lg border border-border bg-card h-40" />
    </div>
  );
}

function AgentProfileHeaderBlock({ profile }: { profile: AgentProfile }) {
  const { header, teams } = profile;
  const teamPills = (teams ?? []).map((t) => ({ id: t.id, name: t.name }));
  return (
    <div className="flex flex-wrap items-center gap-4">
      <div className="h-14 w-14 rounded-full bg-secondary/20 text-secondary text-base font-semibold flex items-center justify-center shrink-0">
        {initialsOf(header.full_name, header.email)}
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="text-2xl font-semibold text-foreground truncate"
          style={{ fontFamily: "var(--app-font-serif)" }}
        >
          {header.full_name || header.email || "—"}
        </div>
        {header.email ? (
          <div className="mt-1 text-sm text-muted-foreground truncate">
            {header.email}
          </div>
        ) : null}
        {teamPills.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            {teamPills.map((t) => (
              <span
                key={t.id}
                className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground"
              >
                {t.name}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LabelChip({
  label,
  count,
  selected,
  onToggle,
}: {
  label: string;
  count: number;
  selected: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={selected}
      className={
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary " +
        (selected
          ? "border-primary bg-primary/10 text-primary"
          : "border-border bg-card text-foreground hover:bg-muted/50")
      }
    >
      <span>{label}</span>
      <span
        className={
          "tabular-nums " +
          (selected ? "text-primary/80" : "text-muted-foreground")
        }
      >
        {count.toLocaleString()}
      </span>
    </button>
  );
}

function CallListRow({
  call,
  onOpen,
}: {
  call: AgentCallRow;
  onOpen: () => void;
}) {
  const tags: { key: string; label: string; tone: "pos" | "neg" | "warn" | "info" }[] = [];
  if (call.positive_sentiment) tags.push({ key: "pos", label: "Positive", tone: "pos" });
  if (call.negative_sentiment) tags.push({ key: "neg", label: "Negative", tone: "neg" });
  if (call.speaking_too_quickly) tags.push({ key: "fast", label: "Fast", tone: "warn" });
  if (call.monologuing) tags.push({ key: "mono", label: "Monologuing", tone: "warn" });
  if (call.quality_call) tags.push({ key: "qual", label: "Quality", tone: "info" });

  const toneClass = (t: "pos" | "neg" | "warn" | "info") =>
    t === "pos"
      ? "bg-primary/10 text-primary"
      : t === "neg"
        ? "bg-destructive/10 text-destructive"
        : t === "warn"
          ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
          : "bg-muted text-muted-foreground";

  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg border border-border bg-card p-3 text-left shadow-sm transition-colors hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        <span className="font-medium text-foreground">
          {call.contact_name || call.contact_phone || "Unknown contact"}
        </span>
        <span className="text-muted-foreground">{fmtStarted(call.started_at)}</span>
        <span className="text-muted-foreground tabular-nums">
          {fmtMmSs(call.duration_ms)}
        </span>
        {call.direction ? (
          <span className="text-xs uppercase tracking-wider text-muted-foreground">
            {call.direction}
          </span>
        ) : null}
        <span className="ml-auto flex flex-wrap items-center gap-1.5">
          {tags.map((t) => (
            <span
              key={t.key}
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs ${toneClass(t.tone)}`}
            >
              {t.label}
            </span>
          ))}
        </span>
      </div>
      {call.recap_summary ? (
        <div className="mt-1 text-xs text-muted-foreground line-clamp-2">
          {call.recap_summary}
        </div>
      ) : null}
    </button>
  );
}

export function AgentProfileBody({
  profile,
  agentKey,
  teamId,
  timeRange,
  rangeMode,
  customRange,
  selectedLabels,
  onToggleLabel,
  onOpenCall,
  showAssessmentsSection,
  onChangeDateRange,
  onSwitchToAllTime,
  onAssignAssessment,
  viewerRole,
  initialFocusedMeasure,
}: {
  profile: AgentProfile;
  agentKey: string;
  teamId: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  // Task #365 — paired YYYY-MM-DD UTC window for the "custom" preset.
  // Only consulted when ``timeRange === "custom"``.
  customRange?: CustomDateRange | null;
  selectedLabels: string[];
  onToggleLabel: (key: string) => void;
  onOpenCall: (callId: string) => void;
  // Ticket 13: "Assessment performance" section. Ticket 19 widens the
  // gate so admin / manager also render it (server-side scope from T17).
  showAssessmentsSection: boolean;
  // Task #290 — forwarded down into AgentAssessmentsSection so a
  // ``mid``/``ln`` deep-link from the TeamsDashboardView TeamAssessmentCard
  // lands with that measure pre-focused.
  initialFocusedMeasure?: MeasureFocus | null;
  // Task #263 — forwarded to AgentAssessmentsSection so the empty
  // state's "Change date range" button can focus the page-level
  // period selector.
  onChangeDateRange?: () => void;
  // Task #264 — forwarded to AgentAssessmentsSection to wire the new
  // cause-aware empty-state actions ("Switch to All time" /
  // "Assign an assessment"). ``viewerRole`` lets the section hide the
  // admin-only action for managers.
  onSwitchToAllTime?: () => void;
  onAssignAssessment?: () => void;
  viewerRole?: Role | null;
}) {
  const { summary, quality, team_average: teamAvg, labels } = profile;
  const positiveSelected = selectedLabels.includes("positive");
  const negativeSelected = selectedLabels.includes("negative");

  // Task #192 — when the agent has no graded calls in the selected
  // range, mirror the Task #191 by-team empty state: show a single
  // italic muted line instead of zero-valued KPI tiles, empty charts
  // and "No data for this range" sub-messages. The agent header and
  // range controls live in the parent so they stay visible.
  const isEmptyRange = summary.assessed_calls === 0;

  const fmtCalls = (n: number | null) =>
    n == null ? "—" : n.toLocaleString(undefined, { maximumFractionDigits: 1 });
  const fmtPct = (n: number | null) => (n == null ? "—" : `${n.toFixed(0)}%`);

  // Sorted label list: built-ins first (in canonical order), then custom
  // moments by count desc. Backend already sorts custom alphabetically by
  // count, but defensively sort here too to keep the picker stable.
  const sortedLabels = useMemo(() => {
    const builtinOrder = [
      "positive",
      "negative",
      "action_items",
      "quality",
      "fast",
      "mono",
    ];
    const builtins = labels.filter((l) => !l.custom);
    const custom = labels.filter((l) => l.custom);
    builtins.sort(
      (a, b) => builtinOrder.indexOf(a.key) - builtinOrder.indexOf(b.key),
    );
    custom.sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
    return [...builtins, ...custom];
  }, [labels]);

  const callsQ = useQuery<AgentCallsOut>({
    queryKey: [
      "agent-calls",
      agentKey,
      teamId,
      timeRange,
      rangeMode,
      customRange?.start ?? null,
      customRange?.end ?? null,
      [...selectedLabels].sort().join(","),
    ],
    queryFn: () =>
      api.agentCalls(agentKey, {
        teamId,
        labels: selectedLabels,
        timeRange,
        rangeMode,
        customRange,
      }),
    enabled: !isEmptyRange,
    refetchInterval: false,
  });

  if (isEmptyRange) {
    return (
      <p className="text-sm text-muted-foreground italic">
        No assessments graded in this period.
      </p>
    );
  }

  return (
    <>
      {/* Headline KPI tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          label="Calls assessed"
          metricId="calls_assessed"
          value={summary.assessed_calls.toLocaleString()}
          sub={
            <span className="text-muted-foreground">
              {`Team avg ${fmtCalls(teamAvg.calls)}`}
            </span>
          }
        />
        <KpiTile
          label="Avg duration"
          metricId="avg_duration"
          value={fmtMmSs(summary.avg_duration_ms)}
          sub={
            <span className="text-muted-foreground">
              {`Team avg ${fmtMmSs(teamAvg.avg_duration_ms)}`}
            </span>
          }
        />
        <KpiTile
          label="Positive sentiment"
          metricId="positive_sentiment"
          value={`${quality.positive_pct.toFixed(0)}%`}
          valueClass="text-primary"
          sub={
            <span className="text-muted-foreground">
              {`Team avg ${fmtPct(teamAvg.positive_pct)}`}
            </span>
          }
          active={positiveSelected}
          onClick={() => onToggleLabel("positive")}
        />
        <KpiTile
          label="Negative sentiment"
          metricId="negative_sentiment"
          value={`${quality.negative_pct.toFixed(0)}%`}
          valueClass="text-destructive"
          sub={
            <span className="text-muted-foreground">
              {`Team avg ${fmtPct(teamAvg.negative_pct)}`}
            </span>
          }
          active={negativeSelected}
          onClick={() => onToggleLabel("negative")}
        />
      </div>

      {/* Dialpad labels and tags — clickable, multi-select AND filter */}
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Dialpad labels and tags
      </div>
      <div className="rounded-lg border border-border bg-card p-4 shadow-md">
        {sortedLabels.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No labels collected on this agent's calls in the selected window.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {sortedLabels.map((l) => (
              <LabelChip
                key={l.key}
                label={l.label}
                count={l.count}
                selected={selectedLabels.includes(l.key)}
                onToggle={() => onToggleLabel(l.key)}
              />
            ))}
          </div>
        )}
      </div>

      {showAssessmentsSection ? (
        <AgentAssessmentsSection
          agentId={agentKey}
          agentName={profile.header.full_name ?? null}
          timeRange={timeRange}
          rangeMode={rangeMode}
          customRange={customRange ?? null}
          onOpenCall={onOpenCall}
          onChangeDateRange={onChangeDateRange}
          onSwitchToAllTime={onSwitchToAllTime}
          onAssignAssessment={onAssignAssessment}
          viewerRole={viewerRole}
          initialFocusedMeasure={initialFocusedMeasure ?? null}
        />
      ) : null}

      <CollapsibleSection
        title={
          <h3 className="text-sm font-semibold text-foreground">Calls</h3>
        }
        rightAdornment={
          <div className="text-xs text-muted-foreground tabular-nums">
            {callsQ.data
              ? `${callsQ.data.rows.length} of ${callsQ.data.total.toLocaleString()}`
              : ""}
          </div>
        }
        storageKey={`agent-profile:calls:${agentKey}`}
        forceOpen={selectedLabels.length > 0}
        bodyClassName="px-4 pb-4 space-y-2"
      >
        {callsQ.isLoading ? (
          <div className="space-y-2 animate-pulse">
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                className="h-16 rounded-lg border border-border bg-card"
              />
            ))}
          </div>
        ) : callsQ.error ? (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
            {(callsQ.error as { message?: string })?.message ||
              "Failed to load calls"}
          </div>
        ) : !callsQ.data || callsQ.data.rows.length === 0 ? (
          // Task #194 — align the Calls list empty state with the
          // Task #192 / Task #191 italic muted line used elsewhere in
          // the agent profile (and on the by-team surfaces) instead
          // of a bordered card with zero-value chrome.
          <p className="text-sm text-muted-foreground italic">
            No calls match the current filters.
          </p>
        ) : (
          <div className="space-y-2">
            {callsQ.data.rows.map((row) => (
              <CallListRow
                key={row.call_id}
                call={row}
                onOpen={() => onOpenCall(row.call_id)}
              />
            ))}
          </div>
        )}
      </CollapsibleSection>
    </>
  );
}

export function AgentProfileView({
  slug,
  agentKey,
  slice,
  teamId,
  onBack,
  onChangeSlice: _onChangeSlice,
  onOpenCall,
  initialTimeRange,
  initialRangeMode,
  initialCustomRange,
  initialFocusedMeasure,
  onRangeChange,
  me,
  onAssignAssessment,
}: {
  slug: string;
  agentKey: string | null;
  slice: string | null;
  teamId: string | null;
  onBack: () => void;
  onChangeSlice: (slice: AgentCallSlice | null) => void;
  onOpenCall: (callId: string, from: string) => void;
  // Ticket 13: URL-state range pre-selection + supadmin role gating
  // for the new "Assessment performance" section. `me` is threaded
  // from App.tsx so this view can role-gate the section.
  initialTimeRange?: TimeRange | null;
  initialRangeMode?: RangeMode | null;
  // Task #365 — paired YYYY-MM-DD UTC bounds for the "custom" preset
  // when this view is restored from a deep link. Both must be present
  // for the custom window to take effect.
  initialCustomRange?: CustomDateRange | null;
  // Task #290 — when this view is opened from a TeamAssessmentCard's
  // focused agent panel, the originating measure is forwarded as a
  // URL deep-link (``mid``/``ln``) and threaded down so the
  // Assessment performance section opens with that bar pre-focused.
  initialFocusedMeasure?: MeasureFocus | null;
  onRangeChange?: (
    timeRange: TimeRange,
    rangeMode: RangeMode,
    customRange?: CustomDateRange | null,
  ) => void;
  me?: UserOut | null;
  // Task #264 — supplied by App.tsx to navigate to the assessments
  // list when an admin clicks the empty-state "Assign an assessment"
  // action. Optional so unit tests can mount the view without wiring
  // a router; the action button is then simply hidden.
  onAssignAssessment?: () => void;
}) {
  const {
    timeRange, customRange, effectiveRangeMode,
    onTimeRangeChange, onRangeModeChange, onCustomRangeChange, onSwitchToAllTime,
  } = useTimeRange({
    initial: {
      timeRange: initialTimeRange,
      rangeMode: initialRangeMode,
      customRange: initialCustomRange,
    },
    sync: "callback",
    onRangeChange,
  });
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [trainingTipsOpen, setTrainingTipsOpen] = useState(false);
  const rangeControlsRef = useRef<HTMLDivElement | null>(null);
  const onChangeDateRange = useCallback(() => {
    const el = rangeControlsRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const firstBtn = el.querySelector<HTMLButtonElement>("button");
    firstBtn?.focus();
  }, []);
  const onToggleLabel = (key: string) => {
    setSelectedLabels((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  const needsResolve = !agentKey;
  const agentsQ = useQuery<AgentRow[]>({
    queryKey: ["agents"],
    queryFn: () => api.agents(),
    enabled: needsResolve,
    refetchInterval: false,
  });
  const resolvedKey = useMemo(() => {
    if (agentKey) return agentKey;
    const match = (agentsQ.data ?? []).find((r) => agentSlug(r) === slug);
    return match ? agentLookupKey(match) : null;
  }, [agentKey, agentsQ.data, slug]);

  const teamArg = teamId ?? undefined;
  const profileQ = useQuery<AgentProfile>({
    queryKey: [
      "agent-profile",
      resolvedKey,
      teamId,
      timeRange,
      effectiveRangeMode,
      customRange?.start ?? null,
      customRange?.end ?? null,
    ],
    queryFn: () =>
      api.agentProfile(
        resolvedKey!,
        teamArg,
        timeRange,
        effectiveRangeMode,
        customRange,
      ),
    enabled: !!resolvedKey,
    refetchInterval: false,
  });

  const isLoading =
    (needsResolve && agentsQ.isLoading) || (!!resolvedKey && profileQ.isLoading);
  const notFound = needsResolve && agentsQ.isSuccess && !resolvedKey;

  // Browser-tab title: the agent's name once the profile loads, with a
  // sensible placeholder while resolving a deep-linked URL.
  useDocumentTitle(
    notFound
      ? "Agent not found"
      : profileQ.data?.header.full_name ||
          profileQ.data?.header.email ||
          "Agent",
  );

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Back to agents
      </button>

      {profileQ.data ? (
        <AgentProfileHeaderBlock profile={profileQ.data} />
      ) : null}

      <div
        ref={rangeControlsRef}
        className="rounded-lg border border-border bg-card p-4 shadow-md"
      >
        <div className="flex flex-wrap items-start justify-between gap-3">
          <TimeRangeControls
            value={timeRange}
            rangeMode={effectiveRangeMode}
            customRange={customRange}
            onValueChange={onTimeRangeChange}
            onRangeModeChange={onRangeModeChange}
            onCustomRangeChange={onCustomRangeChange}
          />
          <button
            type="button"
            onClick={() => setTrainingTipsOpen(true)}
            disabled={!profileQ.data || !resolvedKey}
            className="rounded bg-primary text-primary-foreground text-sm font-medium px-3.5 py-1.5 hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          >
            Training tips
          </button>
        </div>
      </div>

      {profileQ.data && resolvedKey ? (
        <TrainingTipsModal
          open={trainingTipsOpen}
          onClose={() => setTrainingTipsOpen(false)}
          agentId={resolvedKey}
          agentName={
            profileQ.data.header.full_name ||
            profileQ.data.header.email ||
            "Agent"
          }
          rangeFrom={timeRangeToBounds(timeRange, effectiveRangeMode, customRange).from}
          rangeTo={timeRangeToBounds(timeRange, effectiveRangeMode, customRange).to}
          labels={selectedLabels}
          rangeLabel={rangeTitle(timeRange, effectiveRangeMode, customRange)}
          // PET-656 — admins/supadmins get the analysed-call selector;
          // the server enforces the cap from the actor's role regardless.
          isAdmin={me?.role === "supadmin" || me?.role === "admin"}
        />
      ) : null}

      {notFound ? (
        <div className="rounded-lg border border-border bg-card p-6 text-sm text-muted-foreground">
          Agent not found.
        </div>
      ) : isLoading ? (
        <AgentProfileSkeleton />
      ) : profileQ.error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-destructive text-sm">
          {(profileQ.error as { message?: string })?.message ||
            "Failed to load profile"}
        </div>
      ) : profileQ.data && resolvedKey ? (
        <AgentProfileBody
          profile={profileQ.data}
          agentKey={resolvedKey}
          teamId={teamId}
          timeRange={timeRange}
          rangeMode={effectiveRangeMode}
          customRange={customRange}
          selectedLabels={selectedLabels}
          onToggleLabel={onToggleLabel}
          onOpenCall={(callId) =>
            onOpenCall(
              callId,
              encodeAgentFrom(slug, resolvedKey, slice, teamId),
            )
          }
          showAssessmentsSection={
          me?.role === "supadmin" ||
          me?.role === "admin" ||
          me?.role === "manager"
        }
          onChangeDateRange={onChangeDateRange}
          onSwitchToAllTime={onSwitchToAllTime}
          onAssignAssessment={onAssignAssessment}
          viewerRole={me?.role ?? null}
          initialFocusedMeasure={initialFocusedMeasure ?? null}
        />
      ) : null}
    </div>
  );
}

export function AgentAssessmentsSection({
  agentId,
  agentName,
  timeRange,
  rangeMode,
  customRange,
  onOpenCall,
  onChangeDateRange,
  onSwitchToAllTime,
  onAssignAssessment,
  viewerRole,
  initialFocusedMeasure,
}: {
  agentId: string;
  agentName: string | null;
  timeRange: TimeRange;
  rangeMode: RangeMode;
  // Task #365 — paired YYYY-MM-DD UTC window for the "custom" preset.
  // Threaded through timeRangeToBounds so the aggregate + per-result
  // endpoints see the exact same window the parent's KPIs queried.
  customRange?: CustomDateRange | null;
  onOpenCall: (callId: string) => void;
  // Task #290 — opt-in initial focus (forwarded by deep-link URL
  // params). Applied once on first mount in the active range; a
  // subsequent range change still clears focus via the existing
  // ``useEffect`` so a measure that disappears from the new period
  // can't strand the table in an empty state.
  initialFocusedMeasure?: MeasureFocus | null;
  // Task #263 — wired by the parent to scroll/focus the page-level
  // period selector when the user clicks "Change date range" from the
  // empty state. Hidden when the current range is already "All time".
  onChangeDateRange?: () => void;
  // Task #264 — cause-aware empty state actions. Both are optional so
  // the section degrades gracefully if a parent forgets to wire them
  // (the corresponding action button is simply hidden).
  //   * ``onSwitchToAllTime`` flips the page-level period selector to
  //     All time, used when the agent has scored calls all-time but
  //     none in the active window.
  //   * ``onAssignAssessment`` navigates to the assessments list so an
  //     admin can attach an assessment to this agent's team. Rendered
  //     only for admins.
  onSwitchToAllTime?: () => void;
  onAssignAssessment?: () => void;
  // Task #264 — role threaded from AgentProfileView so the empty
  // state can hide the admin-only "Assign an assessment" action for
  // managers and instead show a read-only explainer.
  viewerRole?: Role | null;
}) {
  const { from: rangeFrom, to: rangeTo } = useMemo(
    () => timeRangeToBounds(timeRange, rangeMode, customRange),
    [timeRange, rangeMode, customRange],
  );
  const rangeLabel = rangeTitle(timeRange, rangeMode, customRange);

  // Task #220 — derive a prior-period window of equal length so the
  // summary strip can render a "↑/↓ pts vs prior" trend chip and each
  // bar can paint a prior marker. ``all_time`` has no defined prior
  // so we pass null through and the comparison is omitted (mirrors
  // the TeamsDashboardView triage-list pattern).
  const { priorFrom, priorTo } = useMemo<{
    priorFrom: string | null;
    priorTo: string | null;
  }>(() => {
    if (!rangeFrom || !rangeTo || timeRange === "all_time") {
      return { priorFrom: null, priorTo: null };
    }
    const fromMs = Date.parse(rangeFrom);
    const toMs = Date.parse(rangeTo);
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) {
      return { priorFrom: null, priorTo: null };
    }
    const windowMs = toMs - fromMs;
    return {
      priorFrom: new Date(fromMs - windowMs).toISOString(),
      priorTo: new Date(fromMs).toISOString(),
    };
  }, [rangeFrom, rangeTo, timeRange]);

  const aggQ = useQuery<AgentAggregate>({
    queryKey: [
      "agent-aggregate",
      agentId,
      rangeFrom,
      rangeTo,
      priorFrom,
      priorTo,
    ],
    queryFn: () =>
      api.aggregateForAgent(agentId, {
        from: rangeFrom,
        to: rangeTo,
        prior_from: priorFrom,
        prior_to: priorTo,
      }),
    // Task #246 — ``rangeFrom`` may be ``null`` for the "All time"
    // option (unbounded lower bound); only ``rangeTo`` gates the query.
    enabled: !!agentId && !!rangeTo,
    refetchInterval: false,
    placeholderData: (prev) => prev,
  });

  const aggError = aggQ.error as { status?: number; message?: string } | null;
  const notFound = aggError?.status === 404;

  // Task #217 — focused measure (clicking a bar) + pass/fail sub-toggle
  // drive the results table below. Cleared when the parent range
  // changes so a measure that disappears from the new period can't
  // leave the table in a stale empty state.
  const [focused, setFocused] = useState<MeasureFocus | null>(
    initialFocusedMeasure ?? null,
  );
  const [passFilter, setPassFilter] = useState<"all" | "passed">("passed");
  // Task #290 — when the parent forwards a new deep-link focus (e.g.
  // the user clicks a different focused-row drill on another card),
  // adopt it. Range changes still clear focus below.
  const initialMeasureId = initialFocusedMeasure?.measure_id;
  const initialLabelName = initialFocusedMeasure?.label_name;
  useEffect(() => {
    if (initialMeasureId !== undefined && initialLabelName !== undefined) {
      setFocused({ measure_id: initialMeasureId, label_name: initialLabelName });
    }
  }, [initialMeasureId, initialLabelName]);
  // Task #290 — skip the first run so a deep-link `initialFocusedMeasure`
  // isn't wiped by the range-change clear that would otherwise fire on
  // mount. Subsequent real range changes still clear the focused state.
  const rangeClearRef = useRef(true);
  useEffect(() => {
    if (rangeClearRef.current) {
      rangeClearRef.current = false;
      return;
    }
    setFocused(null);
    setPassFilter("passed");
  }, [rangeFrom, rangeTo]);

  const perLabel = useMemo(() => aggQ.data?.per_label ?? [], [aggQ.data?.per_label]);
  const teamPerLabel = useMemo(() => aggQ.data?.team_per_label ?? [], [aggQ.data?.team_per_label]);
  const callCount = aggQ.data?.call_count ?? 0;
  const priorPerLabel = useMemo(() => aggQ.data?.prior_per_label ?? [], [aggQ.data?.prior_per_label]);
  const priorCallCount = aggQ.data?.prior_call_count ?? 0;
  const summary = useMemo(
    () => summariseMeasures(perLabel, teamPerLabel),
    [perLabel, teamPerLabel],
  );
  // Task #220 — compute the same overall % for the prior period so the
  // summary strip can show a "↑/↓ N pts vs prior" chip. ``null`` when
  // we don't have a paired prior window or the prior period had no
  // graded calls — the chip falls back to an em-dash in that case.
  const priorSummary = useMemo(
    () =>
      priorFrom && priorTo && priorCallCount > 0
        ? summariseMeasures(priorPerLabel)
        : null,
    [priorPerLabel, priorCallCount, priorFrom, priorTo],
  );
  const trendDelta =
    summary && priorSummary
      ? summary.overallPct - priorSummary.overallPct
      : null;
  const hasPriorWindow = !!(priorFrom && priorTo);

  return (
    <CollapsibleSection
      title={
        <h3 className="text-sm font-semibold text-foreground">
          Assessment performance
        </h3>
      }
      rightAdornment={
        <div className="text-xs text-muted-foreground">{rangeLabel}</div>
      }
      storageKey={`agent-profile:assessment-performance:${agentId}`}
      forceOpen={focused !== null || passFilter !== "passed"}
      bodyClassName="p-4 space-y-4 border-t border-border"
    >
      {/* Task #189 — keep the section's layout stable across ranges
          (Daily often has zero graded calls, and the agent may have no
          record at all). Task #263 — when the agent has no graded
          calls in the selected period (including the 404 "no calls on
          record" case), collapse the previous pair of stacked dashed
          placeholders + duplicate caption into a single friendlier
          empty state inside the same panel chrome. */}
      <>
        {aggError && !notFound && (
          <div className="text-sm text-destructive flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              {aggError.message || "Failed to load assessment performance."}
            </span>
            <button
              type="button"
              onClick={() => aggQ.refetch()}
              className="ml-auto inline-flex items-center gap-1 rounded border border-input bg-card px-2 py-0.5 text-xs hover:bg-muted/40"
            >
              Retry
            </button>
          </div>
        )}
        {notFound ? (
          <AssessmentEmptyState
            headline="No calls on record for this agent yet"
            subline="Once this agent has graded calls in the selected period, their assessment scores will appear here."
          />
        ) : !aggQ.isLoading && callCount === 0 ? (
          (() => {
            // Task #264 — diagnostic empty state. The four branches
            // map to the cases in the task spec (in priority order):
            //
            //   3. No assessment is assigned to the agent's team at
            //      all → headline + admin-only "Assign an assessment"
            //      action. Trumps everything else because nothing the
            //      user does to the date range will surface scored
            //      calls until an assessment is wired up.
            //   4. Agent has scored calls all-time but none in the
            //      current window → "Switch to All time" (skipped
            //      when the active range already IS All time).
            //   1. Agent had no visible calls of any kind in this
            //      window → "Change date range" (skipped on All time).
            //   2. Agent had calls in this window but none were
            //      scored → admin-only "Assign an assessment" action,
            //      explainer for managers.
            //
            // We deliberately treat ``undefined`` from older payloads
            // as the conservative defaults (0 / false) so the prior
            // generic empty state never crashes a stale client.
            const firstName = agentFirstName(agentName);
            const totalInPeriod = aggQ.data?.total_calls_in_period ?? 0;
            const scoredAllTime = aggQ.data?.scored_calls_all_time ?? 0;
            const teamHasAssignment =
              aggQ.data?.team_has_assignment ?? false;
            const isAdmin =
              viewerRole === "supadmin" || viewerRole === "admin";
            const isAllTime = timeRange === "all_time";

            if (!teamHasAssignment) {
              return (
                <AssessmentEmptyState
                  headline={`No assessment is assigned to ${firstName}'s team`}
                  subline={
                    isAdmin
                      ? "Assign an assessment to this agent's team so their calls can be scored."
                      : "Ask an admin to assign an assessment to this agent's team so their calls can be scored."
                  }
                  actionLabel={
                    isAdmin && onAssignAssessment
                      ? "Assign an assessment"
                      : undefined
                  }
                  onAction={isAdmin ? onAssignAssessment : undefined}
                />
              );
            }

            if (
              totalInPeriod === 0 &&
              scoredAllTime > 0 &&
              !isAllTime
            ) {
              return (
                <AssessmentEmptyState
                  headline={`${firstName} has ${scoredAllTime.toLocaleString()} scored ${
                    scoredAllTime === 1 ? "call" : "calls"
                  } all-time, but none in this period`}
                  subline="Switch to All time to see this agent's full assessment history."
                  actionLabel={
                    onSwitchToAllTime ? "Switch to All time" : undefined
                  }
                  onAction={onSwitchToAllTime}
                />
              );
            }

            if (totalInPeriod === 0) {
              return (
                <AssessmentEmptyState
                  headline={`${firstName} had no calls in ${rangeLabel.toLowerCase()}`}
                  subline={
                    isAllTime
                      ? "Once this agent has any calls on record, their assessment scores will appear here."
                      : "Try expanding the date range to see whether this agent has any scored calls."
                  }
                  actionLabel={isAllTime ? undefined : "Change date range"}
                  onAction={isAllTime ? undefined : onChangeDateRange}
                />
              );
            }

            // Case 2 — had calls in this period but none were scored.
            return (
              <AssessmentEmptyState
                headline={`${firstName} had ${totalInPeriod.toLocaleString()} ${
                  totalInPeriod === 1 ? "call" : "calls"
                } in this period, but none were scored`}
                subline={
                  isAdmin
                    ? "Check the assessment assignment for this agent's team, or wait for the next scoring run."
                    : "An admin can review the assessment assignment for this agent's team."
                }
                actionLabel={
                  isAdmin && onAssignAssessment
                    ? "Assign an assessment"
                    : undefined
                }
                onAction={isAdmin ? onAssignAssessment : undefined}
              />
            );
          })()
        ) : (
          <>
            {/* Task #217 — manager-friendly summary strip. Hidden when there's
                no data to summarise. Task #220 — trend chip is now wired to
                the prior-period overlay returned by the agent aggregate
                endpoint when the active range has a defined prior window. */}
            {summary && callCount > 0 ? (
              <AssessmentSummaryStrip
                summary={summary}
                inScopeCallCount={callCount}
                trendDelta={trendDelta}
                hasPriorWindow={hasPriorWindow}
                perLabel={perLabel}
                priorPerLabel={priorPerLabel}
                deltaPanelTitle={`What changed — ${agentName ?? "Agent"}`}
                deltaPanelSubtitle={`${rangeLabel} vs prior period`}
              />
            ) : null}
            <CallCountCaption count={callCount} />
            <MeasureBarChart
              rows={perLabel}
              callCount={callCount}
              priorRows={hasPriorWindow ? priorPerLabel : undefined}
              teamAverageRows={teamPerLabel}
              focused={focused}
              onFocus={(f) => {
                setFocused(f);
                if (!f) setPassFilter("passed");
              }}
              groupStorageKey={`agent-profile:${agentId}`}
            />
            {focused ? (
              <>
                <FocusedMeasureControls
                  focused={focused}
                  passFilter={passFilter}
                  onPassFilterChange={setPassFilter}
                  onClear={() => {
                    setFocused(null);
                    setPassFilter("passed");
                  }}
                />
                <AgentResultsTable
                  agentId={agentId}
                  rangeFrom={rangeFrom}
                  rangeTo={rangeTo}
                  onOpenCall={onOpenCall}
                  disabled={false}
                  focused={focused}
                  passFilter={passFilter}
                />
              </>
            ) : (
              <div
                className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 px-4 py-6 text-center text-sm text-muted-foreground italic"
                style={{ minHeight: 80 }}
              >
                Click a topic in the chart above to see its calls.
              </div>
            )}
          </>
        )}
      </>
    </CollapsibleSection>
  );
}

export { AssessmentSummaryStrip };

function AssessmentSummaryStrip({
  summary,
  trendDelta,
  hasPriorWindow,
  perLabel,
  priorPerLabel,
  deltaPanelTitle,
  deltaPanelSubtitle,
  inScopeCallCount,
  scopeLabel = "agent",
}: {
  summary: MeasureSummary;
  // Task #268 — number of in-scope calls the Overall % was computed
  // from. Threaded through so the Overall tooltip can name the
  // sample size in plain English (matches the call count already
  // shown on the card via ``CallCountCaption``).
  inScopeCallCount: number;
  // The strip is reused in agent and team contexts; this controls
  // the subject in the Overall tooltip copy ("this agent's" vs
  // "this team's") so the wording stays accurate in each surface.
  scopeLabel?: "agent" | "team";
  // Task #220 — points-percentage delta (overall_pct − prior_overall_pct).
  // ``null`` when there is no usable prior comparison (no paired prior
  // window, or the prior period had no graded calls) — the chip falls
  // back to an em-dash in that case so the slot stays visible.
  trendDelta?: number | null;
  hasPriorWindow?: boolean;
  // Task #239 — when both per-label rows are present and the prior
  // window has graded calls, the trend chip becomes a button that
  // opens a drill-in side panel listing each measure × label with
  // current rate, prior rate and Δ sorted by biggest movers.
  perLabel?: DeltaPanelRow[];
  priorPerLabel?: DeltaPanelRow[];
  deltaPanelTitle?: string;
  deltaPanelSubtitle?: string;
}) {
  const [panelOpen, setPanelOpen] = useState(false);
  const canDrillIn =
    !!hasPriorWindow &&
    trendDelta != null &&
    Number.isFinite(trendDelta) &&
    !!perLabel &&
    !!priorPerLabel &&
    priorPerLabel.length > 0;
  const subtitle = summary.hasTeamComparison
    ? "vs team average"
    : "by detection rate";
  return (
    <div className="rounded-lg border border-border bg-muted/20 p-3 grid gap-3 md:grid-cols-3">
      <div className="flex flex-col">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">
          Overall
        </div>
        <div className="mt-1 flex items-baseline gap-2">
          {/* Task #268 — explain the headline % in place. The tooltip
              wraps just the figure + caption (not the trend chip,
              which already has its own drill-in / hover behaviour),
              and uses ``inline-flex flex-col items-start`` so the
              caption sits underneath the percentage exactly as
              before — no layout shift. */}
          <Tooltip
            wrapperClassName="inline-flex flex-col items-start"
            content={
              <div className="space-y-1">
                <div>
                  The share of measure checks across this{" "}
                  {scopeLabel}'s {inScopeCallCount.toLocaleString()}{" "}
                  in-scope {inScopeCallCount === 1 ? "call" : "calls"}{" "}
                  that the grader marked as detected.
                </div>
                <div>
                  Calculated as a volume-weighted average across every
                  measure × label pair in scope: each pair contributes
                  proportional to the number of calls it was evaluated
                  against, so a rarely-evaluated measure can't dominate
                  the headline.
                </div>
                <div>
                  Based on {inScopeCallCount.toLocaleString()}{" "}
                  {inScopeCallCount === 1 ? "call" : "calls"} and{" "}
                  {summary.totalEvaluations.toLocaleString()}{" "}
                  {summary.totalEvaluations === 1
                    ? "evaluation"
                    : "evaluations"}
                  .
                </div>
              </div>
            }
            data-testid="assessment-overall-tooltip"
          >
            <span
              tabIndex={0}
              className="inline-flex flex-col items-start focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
            >
              <span
                className="text-2xl font-semibold tabular-nums text-foreground"
                style={{ fontFamily: "var(--app-font-serif)" }}
              >
                {summary.overallPct}%
              </span>
              <span className="text-xs text-muted-foreground">
                measures detected
              </span>
            </span>
          </Tooltip>
          {hasPriorWindow ? (
            <TrendChip
              delta={trendDelta ?? null}
              onClick={canDrillIn ? () => setPanelOpen(true) : undefined}
            />
          ) : null}
        </div>
        {canDrillIn ? (
          <MeasureDeltaPanel
            open={panelOpen}
            title={deltaPanelTitle ?? "What changed"}
            subtitle={deltaPanelSubtitle}
            current={perLabel ?? []}
            prior={priorPerLabel ?? []}
            onClose={() => setPanelOpen(false)}
          />
        ) : null}
      </div>
      <SummaryList
        title="Top strengths"
        subtitle={subtitle}
        items={summary.strengths}
        tone="positive"
        emptyText="—"
      />
      <SummaryList
        title="Needs attention"
        subtitle={subtitle}
        items={summary.needsAttention}
        tone="negative"
        emptyText="—"
      />
    </div>
  );
}

// Task #220 — small trend chip next to the overall %. ``delta`` is in
// points-percentage (e.g. ``4`` for "↑ 4 pts"). ``null`` falls back
// to an em-dash so the slot stays visible when the prior window had
// no graded calls.
function TrendChip({
  delta,
  onClick,
}: {
  delta: number | null;
  // Task #239 — when provided, the chip becomes a button that opens
  // the per-measure delta drill-in panel. Omitted when there's no
  // usable prior comparison to drill into.
  onClick?: () => void;
}) {
  const baseCls =
    "inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium";
  const clickableCls = onClick
    ? " cursor-pointer hover:brightness-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    : "";
  if (delta === null || !Number.isFinite(delta)) {
    const cls = `${baseCls} bg-muted/40 text-muted-foreground`;
    return (
      <span
        className={cls}
        title="No prior-period comparison available"
        data-trend="none"
      >
        — vs prior
      </span>
    );
  }
  const rounded = Math.round(delta);
  let toneCls: string;
  let label: string;
  let trendAttr: "up" | "down" | "flat";
  if (rounded === 0) {
    toneCls = "bg-muted/40 text-muted-foreground";
    label = "↔ 0 pts vs prior";
    trendAttr = "flat";
  } else {
    const up = rounded > 0;
    toneCls = up
      ? "bg-primary/15 text-primary"
      : "bg-destructive/15 text-destructive";
    label = `${up ? "↑" : "↓"} ${Math.abs(rounded)} pts vs prior`;
    trendAttr = up ? "up" : "down";
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseCls} ${toneCls}${clickableCls}`}
        data-trend={trendAttr}
        aria-label={`${label} — show what changed`}
        title="Show what changed"
      >
        {label}
      </button>
    );
  }
  return (
    <span
      className={`${baseCls} ${toneCls}`}
      data-trend={trendAttr}
    >
      {label}
    </span>
  );
}

function SummaryList({
  title,
  subtitle,
  items,
  tone,
  emptyText,
}: {
  title: string;
  subtitle?: string;
  items: SummaryRow[];
  tone: "positive" | "negative";
  emptyText: string;
}) {
  const pctClass =
    tone === "positive"
      ? "text-primary"
      : "text-destructive";
  return (
    <div className="flex flex-col">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">
        {title}
      </div>
      {subtitle ? (
        <div className="text-[11px] text-muted-foreground">{subtitle}</div>
      ) : null}
      {items.length === 0 ? (
        <div className="mt-1 text-sm text-muted-foreground">{emptyText}</div>
      ) : (
        <ul className="mt-1 space-y-0.5">
          {items.map((it) => {
            // Show ± delta in points vs team avg when available so a
            // manager sees both the absolute rate ("38%") and how
            // far off the team it is ("-22 pts").
            const sign = it.deltaPct == null ? "" : it.deltaPct > 0 ? "+" : "";
            // Task #268 — per-row tooltip explains the two numbers in
            // plain English: the detection rate (with the sample
            // size it was computed from) and, when available, the
            // ± pts vs the team average for the same measure × label
            // over the same window. When no team-average reference
            // exists for this row, the tooltip says so and omits the
            // ± pts paragraph.
            const deltaParagraph =
              it.hasTeamReference && it.deltaPct != null ? (
                it.deltaPct === 0 ? (
                  <div>
                    <strong>0 pts</strong> = exactly matches the team
                    average for this measure over the same window.
                  </div>
                ) : it.deltaPct > 0 ? (
                  <div>
                    <strong>+{it.deltaPct} pts</strong> ={" "}
                    {it.deltaPct} percentage{" "}
                    {it.deltaPct === 1 ? "point" : "points"} above the
                    team average for the same measure over the same
                    window.
                  </div>
                ) : (
                  <div>
                    <strong>−{Math.abs(it.deltaPct)} pts</strong> ={" "}
                    {Math.abs(it.deltaPct)} percentage{" "}
                    {Math.abs(it.deltaPct) === 1 ? "point" : "points"}{" "}
                    below the team average for the same measure over
                    the same window.
                  </div>
                )
              ) : (
                <div>
                  No team-average comparison is available for this
                  measure in the selected period, so no ± pts is
                  shown.
                </div>
              );
            return (
              <li
                key={it.label}
                className="text-sm"
              >
                <Tooltip
                  wrapperClassName="flex w-full"
                  content={
                    <div className="space-y-1">
                      <div>
                        <strong>{it.pct}%</strong> = detected on{" "}
                        {it.pct}% of the {it.calls.toLocaleString()}{" "}
                        {it.calls === 1 ? "call" : "calls"} this
                        measure was checked on.
                      </div>
                      {deltaParagraph}
                    </div>
                  }
                  data-testid="assessment-row-tooltip"
                >
                  <span
                    tabIndex={0}
                    className="flex w-full items-baseline justify-between gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
                  >
                    <span className="truncate text-foreground">{it.label}</span>
                    <span className="shrink-0 inline-flex items-baseline gap-1.5 tabular-nums">
                      {it.deltaPct != null ? (
                        <span className={`text-xs ${pctClass}`}>
                          {sign}
                          {it.deltaPct} pts
                        </span>
                      ) : null}
                      <span className={pctClass}>{it.pct}%</span>
                    </span>
                  </span>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Task #286 — exported so the Teams view "By Assessment" surface can
// reuse the exact same Focused / Present only / Clear filter chip
// group under a team's measure chart.
export { FocusedMeasureControls };

function FocusedMeasureControls({
  focused,
  passFilter,
  onPassFilterChange,
  onClear,
}: {
  focused: MeasureFocus;
  passFilter: "all" | "passed";
  onPassFilterChange: (v: "all" | "passed") => void;
  onClear: () => void;
}) {
  const options: { value: "all" | "passed"; label: string }[] = [
    { value: "all", label: "All" },
    { value: "passed", label: "Present only" },
  ];
  return (
    <div
      className="flex flex-wrap items-center gap-2 text-xs"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.stopPropagation();
          onClear();
        }
      }}
    >
      <span className="text-muted-foreground">
        Focused: <span className="text-foreground font-medium">{focused.label_name}</span>
      </span>
      <div
        role="group"
        aria-label="Outcome filter"
        className="inline-flex rounded-md border border-input bg-card overflow-hidden"
      >
        {options.map((opt) => {
          const active = opt.value === passFilter;
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onPassFilterChange(opt.value)}
              aria-pressed={active}
              className={`px-2.5 py-1 border-r border-border last:border-r-0 ${
                active
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted/40"
              }`}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="rounded border border-input bg-card px-2 py-1 text-foreground hover:bg-muted/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        Clear filter
      </button>
    </div>
  );
}

function ExpandedRow({
  row,
  onRetry,
  retryPending,
  retryError,
  retryQueued,
  retryBlocked,
  onOpenCall,
}: {
  row: AgentAssessmentResultRow;
  onRetry: () => void;
  retryPending: boolean;
  retryError: string | null;
  retryQueued: boolean;
  retryBlocked: boolean;
  onOpenCall: (callId: string) => void;
}) {
  if (row.status === "failed") {
    const disabled =
      retryPending || retryQueued || retryBlocked || !row.run_id;
    return (
      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
        <div className="text-sm text-destructive">{row.error}</div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onRetry}
            disabled={disabled}
            className="inline-flex items-center rounded border border-border bg-card px-3 py-1 text-xs font-medium text-foreground shadow-sm transition-colors hover:bg-muted/50 disabled:cursor-not-allowed disabled:opacity-60 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {retryPending
              ? "Retrying…"
              : retryQueued
                ? "Re-grading…"
                : "Retry"}
          </button>
          {!row.run_id && (
            <span className="text-xs text-muted-foreground">
              No source run recorded — retry unavailable.
            </span>
          )}
          {retryQueued && (
            <span className="text-xs text-muted-foreground">
              Re-grade in progress. The row will refresh when it finishes.
            </span>
          )}
          {retryBlocked && !retryQueued && (
            <span className="text-xs text-muted-foreground">
              Another retry is in progress.
            </span>
          )}
          {retryError && (
            <span className="text-xs text-destructive">{retryError}</span>
          )}
        </div>
      </div>
    );
  }
  const { absent, total } = scoreOf(row);
  const tokens = row.input_tokens + row.output_tokens;
  return (
    <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
      <div className="flex flex-wrap items-center gap-2">
        <SentimentChip row={row} />
        {total > 0 ? (
          <span
            className="text-xs text-muted-foreground tabular-nums"
            aria-label={`${absent} absent of ${total} measures`}
          >
            {absent} absent / {total}
          </span>
        ) : null}
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {tokens.toLocaleString()} tokens
        </span>
      </div>
      {row.summary ? (
        <div className="text-sm text-foreground whitespace-pre-wrap">
          {row.summary}
        </div>
      ) : null}
      <div className="text-xs text-muted-foreground inline-flex gap-3">
        <span>v{row.assessment_version}</span>
        <span>·</span>
        <span>Graded {relativeTime(row.created_at)}</span>
      </div>
      <div>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenCall(row.call_id);
          }}
          className="text-sm text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
        >
          View call detail →
        </button>
      </div>
    </div>
  );
}

type SortKey = "started" | "score";
type SortDir = "asc" | "desc";

function scoreOf(r: AgentAssessmentResultRow): {
  absent: number;
  total: number;
  pct: number | null;
} {
  let absent = 0;
  let total = 0;
  for (const m of r.measures ?? []) {
    for (const v of Object.values(m.label_outcomes ?? {})) {
      total += 1;
      if (!v) absent += 1;
    }
  }
  const pct = total > 0 ? Math.round(((total - absent) / total) * 100) : null;
  return { absent, total, pct };
}

function AgentResultsTable({
  agentId,
  rangeFrom,
  rangeTo,
  onOpenCall,
  disabled = false,
  focused = null,
  passFilter = "passed",
}: {
  agentId: string;
  // Task #246 — ``rangeFrom`` is nullable to allow the Agent View's
  // "All time" option, which queries every scored call on record
  // (no lower-bound filter applied server-side).
  rangeFrom: string | null;
  rangeTo: string;
  onOpenCall: (callId: string) => void;
  disabled?: boolean;
  // Task #217 — when set, the table filters to calls evaluated for the
  // focused (measure_id, label_name) pair, optionally restricted by
  // the parent's pass/fail toggle.
  focused?: MeasureFocus | null;
  passFilter?: "all" | "passed";
}) {
  const [sortKey, setSortKey] = useState<SortKey>("started");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const toggleSort = (k: SortKey) => {
    if (sortKey === k) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(k);
      setSortDir(k === "started" ? "desc" : "asc");
    }
  };
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const toggleExpanded = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const qc = useQueryClient();
  const resultsQ = useQuery<AgentAssessmentResultRow[]>({
    queryKey: ["assessment-results-by-agent", agentId, rangeFrom, rangeTo],
    queryFn: () =>
      api.assessmentResultsByAgent(agentId, {
        from: rangeFrom,
        to: rangeTo,
      }),
    // Task #246 — ``rangeFrom`` may be ``null`` for the "All time"
    // option (unbounded lower bound); only ``rangeTo`` gates the query.
    enabled: !disabled && !!agentId && !!rangeTo,
    refetchInterval: false,
  });

  // Per-row retry state. Mirrors the Phase 3 retry path: kicks off
  // `assessmentRunRetryFailed` against the row's source run, then
  // polls the new run's status until it finishes and invalidates the
  // results + aggregate queries so the row refreshes in place once
  // the new result lands. Backend rejects concurrent runs (409), so
  // at most one retry can be in flight at a time.
  const [pendingRetry, setPendingRetry] = useState<{
    rowId: string;
    newRunId: string;
  } | null>(null);
  const [errorByRow, setErrorByRow] = useState<Record<string, string>>({});

  const pendingStatusQ = useQuery<AssessmentRunStatus>({
    queryKey: ["assessment-run-status", pendingRetry?.newRunId],
    queryFn: () =>
      api.assessmentRunStatus(pendingRetry!.newRunId),
    enabled: !!pendingRetry?.newRunId,
    refetchInterval: (q) => {
      const st = q.state.data?.status;
      if (st === "completed" || st === "partial" || st === "failed") {
        return false;
      }
      return 2000;
    },
  });

  useEffect(() => {
    if (!pendingRetry) return;
    const st = pendingStatusQ.data?.status;
    if (st === "completed" || st === "partial" || st === "failed") {
      qc.invalidateQueries({
        queryKey: ["assessment-results-by-agent", agentId],
      });
      qc.invalidateQueries({ queryKey: ["agent-aggregate", agentId] });
      qc.invalidateQueries({ queryKey: ["assessment-run-active"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-latest"] });
      qc.invalidateQueries({ queryKey: ["assessment-runs-history"] });
      setPendingRetry(null);
    }
  }, [pendingRetry, pendingStatusQ.data, qc, agentId]);

  const retryM = useMutation({
    mutationFn: async (args: { rowId: string; sourceRunId: string }) => {
      const res = await api.assessmentRunRetryFailed(args.sourceRunId);
      return { ...res, rowId: args.rowId };
    },
    onSuccess: (data) => {
      setErrorByRow((prev) => {
        if (!(data.rowId in prev)) return prev;
        const next = { ...prev };
        delete next[data.rowId];
        return next;
      });
      setPendingRetry({ rowId: data.rowId, newRunId: data.run_id });
      qc.invalidateQueries({ queryKey: ["assessment-run-active"] });
      qc.invalidateQueries({ queryKey: ["assessment-run-latest"] });
      qc.invalidateQueries({ queryKey: ["assessment-runs-history"] });
    },
    onError: (err: unknown, vars) => {
      const msg =
        (err as { message?: string } | null)?.message ?? "Retry failed";
      setErrorByRow((prev) => ({ ...prev, [vars.rowId]: msg }));
    },
  });

  const handleRetry = (row: AgentAssessmentResultRow) => {
    if (!row.run_id) return;
    if (retryM.isPending) return;
    if (pendingRetry) return;
    setErrorByRow((prev) => {
      if (!(row.id in prev)) return prev;
      const next = { ...prev };
      delete next[row.id];
      return next;
    });
    retryM.mutate({ rowId: row.id, sourceRunId: row.run_id });
  };

  // Task #217 — hooks must run unconditionally on every render, so the
  // filter + sort memoisation lives above the disabled / loading /
  // error early returns. ``allRows`` defaults to an empty array when
  // the query hasn't loaded yet, which keeps the memos cheap on those
  // passes.
  const allRows = useMemo(() => resultsQ.data ?? [], [resultsQ.data]);
  // Task #217 — apply focus + pass/fail filter. A row is "evaluated for"
  // the focused (measure_id, label_name) iff the focused measure id is
  // present on it and its ``label_outcomes`` includes the focused label.
  // Failed grader runs are excluded entirely when a focus is active
  // because they have no per-measure outcome to filter against.
  const rows = useMemo(() => {
    if (!focused) return allRows;
    return allRows.filter((r) => {
      const m = (r.measures ?? []).find(
        (x) => x.measure_id === focused.measure_id,
      );
      if (!m) return false;
      if (!(focused.label_name in (m.label_outcomes ?? {}))) return false;
      const outcome = m.label_outcomes[focused.label_name];
      if (passFilter === "passed") return outcome === true;
      return true;
    });
  }, [allRows, focused, passFilter]);

  // Agent's average call score across all loaded calls in the period.
  // Used as the baseline for the per-row "Δ vs. average" badge so the
  // row mirrors the team drill-down "Agents · overall" delta treatment.
  // Computed unconditionally above any early returns to keep hook order
  // stable across renders.
  const agentAvg = useMemo(() => {
    let totSum = 0;
    let totCount = 0;
    for (const r of allRows) {
      if (r.status === "failed") continue;
      const { pct } = scoreOf(r);
      if (pct === null) continue;
      totSum += pct / 100;
      totCount += 1;
    }
    return totCount > 0 ? totSum / totCount : null;
  }, [allRows]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === "asc" ? 1 : -1;
    const out = [...rows];
    out.sort((a, b) => {
      if (sortKey === "started") {
        return (
          (new Date(a.started_at).getTime() -
            new Date(b.started_at).getTime()) *
          dir
        );
      }
      // score: order by absent-count, then by score pct as tiebreak.
      const sa = scoreOf(a);
      const sb = scoreOf(b);
      if (sa.absent !== sb.absent) return (sa.absent - sb.absent) * dir;
      const pa = sa.pct ?? -1;
      const pb = sb.pct ?? -1;
      return (pa - pb) * dir;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  if (disabled) {
    // Task #189 — parent signalled the agent has no calls on record at
    // all (404). Render a stable placeholder so the section keeps the
    // same chrome instead of collapsing to a single line of text.
    return (
      <div
        className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 text-sm text-muted-foreground italic"
        style={{ minHeight: 120 }}
      >
        No assessment results to show.
      </div>
    );
  }
  if (resultsQ.isLoading) {
    // Task #189 — keep a stable height so the section's layout doesn't
    // collapse while results are loading for a new range.
    return (
      <div
        className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 text-sm text-muted-foreground italic"
        style={{ minHeight: 120 }}
      >
        Loading…
      </div>
    );
  }
  if (resultsQ.error) {
    return (
      <div
        className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 text-sm text-destructive"
        style={{ minHeight: 120 }}
      >
        Failed to load results.
      </div>
    );
  }
  if (allRows.length === 0) {
    return (
      <div
        className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 text-sm text-muted-foreground italic"
        style={{ minHeight: 120 }}
      >
        No assessment results in this period.
      </div>
    );
  }

  const filterLabel = focused
    ? `${focused.label_name}${passFilter === "passed" ? " present" : ""}`
    : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div
          className="text-xs text-muted-foreground tabular-nums"
          aria-live="polite"
        >
          Showing {sortedRows.length} of {allRows.length} calls
          {filterLabel ? ` — ${filterLabel}` : ""}
        </div>
        <SortToggle
          sortKey={sortKey}
          sortDir={sortDir}
          onToggle={toggleSort}
        />
      </div>
      {sortedRows.length === 0 ? (
        <div
          className="flex items-center justify-center rounded border border-dashed border-border bg-muted/20 text-sm text-muted-foreground italic"
          style={{ minHeight: 120 }}
        >
          No calls match the current filter.
        </div>
      ) : (
        <div className="space-y-1">
          {sortedRows.map((r) => {
            const isExpanded = expanded.has(r.id);
            const { pct } = scoreOf(r);
            const delta =
              pct !== null && agentAvg !== null ? pct / 100 - agentAvg : null;
            return (
              <div key={r.id}>
                <button
                  type="button"
                  onClick={() => toggleExpanded(r.id)}
                  aria-expanded={isExpanded}
                  className="w-full flex items-center justify-between gap-3 rounded px-3 py-2 text-left hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
                >
                  <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap shrink-0 w-32">
                    {fmtStarted(r.started_at)}
                  </span>
                  <span
                    className="text-sm text-foreground truncate flex-1"
                    title={r.assessment_name}
                  >
                    {r.assessment_name}
                  </span>
                  {r.status === "failed" ? (
                    <span className="text-xs text-destructive tabular-nums shrink-0">
                      Failed
                    </span>
                  ) : pct !== null ? (
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      <span className="text-foreground font-medium">
                        {pct}%
                      </span>
                      {delta !== null ? (
                        <>
                          {" "}
                          <span
                            className={
                              delta >= 0
                                ? "text-emerald-600"
                                : "text-destructive"
                            }
                            aria-label={`${formatDeltaPp(delta) || "0pp"} versus agent average`}
                          >
                            ({formatDeltaPp(delta) || "0pp"})
                          </span>
                        </>
                      ) : null}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground tabular-nums shrink-0">
                      —
                    </span>
                  )}
                  <ArrowRight
                    className={`h-4 w-4 text-muted-foreground shrink-0 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                    aria-hidden="true"
                  />
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3 pt-2">
                    <ExpandedRow
                      row={r}
                      onRetry={() => handleRetry(r)}
                      retryPending={
                        retryM.isPending &&
                        retryM.variables?.rowId === r.id
                      }
                      retryError={errorByRow[r.id] ?? null}
                      retryQueued={pendingRetry?.rowId === r.id}
                      retryBlocked={
                        !!pendingRetry && pendingRetry.rowId !== r.id
                      }
                      onOpenCall={onOpenCall}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SortToggle({
  sortKey,
  sortDir,
  onToggle,
}: {
  sortKey: SortKey;
  sortDir: SortDir;
  onToggle: (k: SortKey) => void;
}) {
  const arrow = sortDir === "asc" ? "▲" : "▼";
  const keys: { k: SortKey; label: string }[] = [
    { k: "started", label: "Started" },
    { k: "score", label: "Score" },
  ];
  return (
    <div className="flex items-center gap-1 text-xs text-muted-foreground">
      <span>Sort</span>
      {keys.map(({ k, label }) => {
        const active = sortKey === k;
        return (
          <button
            key={k}
            type="button"
            onClick={() => onToggle(k)}
            aria-pressed={active}
            className={`rounded px-1.5 py-0.5 hover:bg-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring ${active ? "text-foreground font-medium" : ""}`}
          >
            {label}
            {active ? <span aria-hidden="true"> {arrow}</span> : null}
          </button>
        );
      })}
    </div>
  );
}

function SentimentChip({ row }: { row: AgentAssessmentResultRow }) {
  if (row.positive_sentiment && !row.negative_sentiment) {
    return (
      <span className="inline-flex items-center rounded-full bg-primary/15 px-2 py-0.5 text-xs text-primary">
        Positive
      </span>
    );
  }
  if (row.negative_sentiment && !row.positive_sentiment) {
    return (
      <span className="inline-flex items-center rounded-full bg-destructive/15 px-2 py-0.5 text-xs text-destructive">
        Negative
      </span>
    );
  }
  if (row.positive_sentiment && row.negative_sentiment) {
    return (
      <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Mixed
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}
